import { format as tzFormat, formatInTimeZone, zonedTimeToUtc, utcToZonedTime } from 'date-fns-tz';
import { format as fnFormat, differenceInSeconds, parseISO, getHours, getMinutes, getSeconds, parse, formatISO, startOfWeek } from 'date-fns';
 
import { typeChecker } from './typeChecker';
 

// This makes a ISO time stamp out of StartDate and StartTime
// example startDate: 2023-11-15, startTime: 01:50:42 will become 2023-11-15T01:50:42+00:00
// if you provide a timezone, it will add the offest to the timestamp but will convert the time.
// this function only compbines start and end time and adds the offset to the end of the timestamp
export const combineDateAndTimeToISOTimeStamp = (
  dateStamp: string,
  timeStamp: string,
  options?: {
    timeZone?: string;
    convertToZonedTime?: boolean;
  }
): string => {
  const newStamp = `${dateStamp}T${timeStamp}`;
  // add the UTC offset as default
  let offset = '+00:00';
  // make a utc timestamp
  const utcStamp = `${newStamp}${offset}`;
  // sometimes the time has already been zoned, so we jus want to add the offset to the end,
  // not convert it
  if (options?.timeZone) {
    // Format the converted date with the new time zone offset
    if (options?.convertToZonedTime)
      return convertUTCTimestamptoZonedTimestamp(utcStamp, options?.timeZone) as string;
    else offset = formatInTimeZone(new Date(), options.timeZone, 'XXX');
  }

  return `${newStamp}${offset}`;
};

// add offset text to timestamp
// example timestamp: 2023-11-15T01:50:42 will become 2023-11-15T01:50:42+00:00 or -05:00 depending on the provided timezone
export const addOffsetTextToTimestamp = (
  timeStamp = '2025',
  options?: {
    timeZone?: string;
    convertToZonedTime?: boolean;
  }
): string => {
  const { timeZone } = options || {};

  const time = timeStamp.split('T')[1]
  if (!time) timeStamp += `T00:00:00`

  const offset = timeZone ? convertToZonedTimestamp(new Date(), timeZone)?.slice(-6) : '+00:00';

  return timeStamp + offset;
};

// takes a UTC timestamp and a timezone, applies the offset to the time and adds the offset to the timestamp then returns a new ISO timestamp with zoned time
// EXAMPLE --------------
// UTC timestamp: 2023-11-15T01:50:42+00:00
// Timezone: Pacific/Honolulu (which is -10 hours from utc)
// will return the string 2023-11-14T15:50:42-10:00

const formatters: Record<string, string> = {
  'chart-axis':'M/dd',
  'fullDay': `EEEE, d MMM, p`, // Saturday, 12 Apr 2025, 1:30 PM
  'full':`do MMM y, p`, // 12 Apr 2025, 1:30 PM
  'all':`do MMM y, h:mm:ss a`, // 12 Apr 2025, 1:30 PM
  'dayTime': `do MMM, p`, // 12 Apr, 1:30 PM
  'date':'d MMM y', // 12 Apr 2025
  'dateSm':'d MMM', // 12 Apr 
  'axis':'do MMM', // 12 Apr 
  'weekDay':`EEEE, d MMM y`, // Saturday, 12 Apr 2025
  // Aggregator formats
  'WEEKsm': `w`,
  'WEEK': `d MMM y`, // 12 Apr 2025
  'MONTH': `MMM y`, // Apr 2025
  'MONTHsm': 'd MMM', // 12 Apr
  'DAY': `EEEE, d MMM y`, // Friday, 12 Apr 2025
  'DAYsm': `d MMM`, // 12 Apr,
  'AXISday': `do MMM`, // 12 Apr
  'timeSm': `d MMM - h:mm a`, //12 Apr 2025 - 01:30 PM,
  'timeLm': `h:mm:ss a` //12:23:32 AM,
  
}
export const convertToZonedTimestamp = (
  timestamp: string | Date | number,
  timeZone?: string,
  format?: string | keyof typeof formatters
): string | undefined => {
  timeZone = timeZone || 'UTC'
  timestamp = timestamp || new Date()
  let utcz = false
   const isLocal = timeZone === 'local' ? true : false;
 
  if (timeZone === 'UTCz' || isLocal) {
    timeZone = 'UTC'
    utcz = true
  }

  if (timestamp instanceof Date) timestamp = isLocal ? timestamp.toISOString() : zonedTimeToUtc(timestamp, timeZone).toISOString();
  
  format = format || `yyyy-MM-dd'T'HH:mm:ssXXX`
  if (format) format = formatters?.[format] || format
  // set default format, zoned time with offset
  
  if (!timestamp || timestamp === '') return 'error'
  if (utcz) return new Date(timestamp).toISOString()
  // Format the converted date with the new time zone offset

  let formattedDate = isLocal ? fnFormat(new Date(timestamp || 1234), format) : formatInTimeZone(timestamp, timeZone, format)
  if (!utcz && !isLocal) formattedDate = formattedDate.includes('Z') ? formattedDate.replace("Z", "-00:00") : formattedDate
   
  return formattedDate;
};

// renaming above function to be more clear
export const convertUTCTimestamptoZonedTimestamp = convertToZonedTimestamp;

// get the number of seconds between a start and end time, provided in ISO string format
// and return a number value of the amount of seconds between the 2
export const getDurationISOTimeStamps = (start: string, end: string): number =>
  differenceInSeconds(parseISO(start), parseISO(end));

// get user locale based on browser
export const getUserLocale = (): string =>
  navigator.languages.length ? navigator.languages[0] : navigator.language;

export interface GetCUrrentZonedTimeReturnProps {
  string?: string;
  date: Date;
  utc: string;
  zoned: string;
}

export interface GenerateZonedStartTimeProps {
  subtractSeconds?: number;
  subtractDays?: number;
  subtractHours?: number;
  subtractMinutes?: number;
  subtractWeeks?: number;
  subtractMonths?: number;
  startOf?: 'day' | 'week' | 'month' | 'year';
  endOfDay?: boolean;
  /** custom format, please us date-fns format string options , like 'YYYY/MM/DD or P, p  etc' */
  format?: string;
  returnUtc?: boolean;
  /** set this to true to subract a second to keep the same day as the start timestamp  */
  isEnd?: boolean

}

export const generateZonedTimeframe = (
  timeZone: string,
  {
    subtractDays,
    subtractSeconds,
    subtractHours,
    subtractMinutes,
    subtractWeeks,
    subtractMonths,
    startOf,
    //endOfDay
  }: GenerateZonedStartTimeProps,
  returnUtc?: boolean
): {
  startTime: string;
  endTime: string;
} => {

  const start = generateZonedTimestamp(timeZone, {
    subtractDays,
    subtractSeconds,
    subtractHours,
    subtractMinutes,
    subtractWeeks,
    subtractMonths,
    startOf
  });

  const end = getCurrentZonedTime(timeZone);
 
 
  return {
    startTime: returnUtc ? start.utc : start.zoned,
    endTime: returnUtc ? end.utc : end.zoned
  };
};

// returns a zone and utc string timestamp for start of day
export const getZonedStartOfDay = (
  timeZone?: string
): {
  zoned: string;
  utc: string;
  date: Date;
  formatted: string;
} => {
  const hasUTCz = timeZone === 'UTCz' ? true : false;
  //const isLocal = timeZone === 'local' ? true : false;
  if (hasUTCz) timeZone = 'UTC'
  timeZone = timeZone || 'UTC';
  
  const newStart = new Date()
  // Get the current date and time
  const zonedTimestampArr = (convertToZonedTimestamp(newStart, hasUTCz ? 'UTCz' : timeZone) as string).split('T');
  const dateStr = zonedTimestampArr[0];
  const offesetStr = zonedTimestampArr[1].slice(-6);

  const newTimestamp = `${dateStr}T00:00:00${offesetStr}`;
  const newUTCTimestamp = convertToZonedTimestamp(newTimestamp, hasUTCz ? 'UTCz' : 'UTC') as string;

  return {
    zoned: newTimestamp,
    utc: newUTCTimestamp,
    date: new Date(newTimestamp),
    formatted: convertToZonedTimestamp(newTimestamp, hasUTCz ? 'UTCz' : timeZone, 'P, p') as string
  };
};

// remove offset from the timestamp and return a new date object
export const removeOffsetFromTimestamp = (timestamp: string | number | Date): Date | string | number | undefined => {
  const type = typeChecker(timestamp)
  let stringStamp = timestamp  
 
  if (type.date) stringStamp = (timestamp as Date).toISOString()
  if (type.number) stringStamp = new Date(timestamp as number).toISOString()
  // don't know what this is, or nothing is there so we'll just send it back
  if (stringStamp !== 'string') return stringStamp


  return stringStamp
}

 
// this will return a new timestamp with a iso string version of the zoned time and utc time
export const generateZonedTimestamp = (
  timeZone: string,
  {
    subtractSeconds,
    subtractDays,
    subtractHours,
    subtractMinutes,
    subtractWeeks,
    subtractMonths,
    format,
    startOf,
    isEnd,
  }: GenerateZonedStartTimeProps
): {
  utc: string;
  zoned: string;
  date: Date;
  formatted: string;
} => {
  timeZone = timeZone || 'UTC';
  const isLocal = timeZone === 'local' ? true : false;
  const hasUTCz = timeZone === 'UTCz' || isLocal ? true : false;
  
  // Get the current date and time
  if (hasUTCz)  timeZone = 'UTC'
  

  subtractSeconds = subtractSeconds || 0;
  // default format is 04/22/2024, 6:58 PM
  format = format || 'P, p';

  let newTimestamp = isLocal ? new Date().getTime() : getCurrentZonedTime(timeZone).date.getTime();

  if (startOf || subtractDays || subtractWeeks || subtractMonths) {
    if (startOf === 'week' || subtractWeeks) {
      subtractWeeks = subtractWeeks || 0
      const startOfWeekDate = startOfWeek(utcToZonedTime(new Date(), timeZone), { weekStartsOn: 1 }); // Assuming week starts on Monday
      newTimestamp = zonedTimeToUtc(startOfWeekDate, timeZone).getTime();
      if (subtractDays) subtractDays = Number(subtractDays) - 1
    } else if (startOf === 'month' || subtractMonths) {
      subtractMonths = subtractMonths || 0
      const startOfMonthDate = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
      newTimestamp = zonedTimeToUtc(startOfMonthDate, timeZone).getTime();
      if (subtractMonths) subtractMonths = Number(subtractMonths) - 1
    } else {      
      newTimestamp = getZonedStartOfDay(timeZone).date.getTime()
      if (subtractDays) subtractDays = Number(subtractDays) - 1
    }      
  }

  if (subtractMinutes) subtractSeconds += subtractMinutes * 60;
  if (subtractHours) subtractSeconds += subtractHours * 3600;
  if (subtractDays) subtractSeconds += subtractDays * 86400;
  if (subtractWeeks) subtractSeconds += subtractWeeks * 604800;
  if (subtractMonths) subtractSeconds += subtractMonths * 2628000;
  
  
  // do this to convert to ms for adding and subtracting time frames
  subtractSeconds *= 1000  // subtract a second to keep the same day as the start timestamp

  // subtract the amount of ms from current utc ms
  const subtractedUtcTime = new Date(newTimestamp - subtractSeconds - (isEnd ? 1000 : 0))
  const utcIso = subtractedUtcTime.toISOString();

  
  const range =
    startOf === 'day'
      ? getZonedStartOfDay(hasUTCz ? 'UTCz' : timeZone)
      : {
          utc: utcIso, //convertToZonedTimestamp(subtractedUtcTime, 'UTC') as string as string,
          zoned: convertToZonedTimestamp(utcIso, hasUTCz || isLocal ? 'UTCz' : timeZone) as string,
          date: subtractedUtcTime,
          formatted: convertToZonedTimestamp(utcIso, hasUTCz || isLocal ? 'UTCz' : timeZone, format) as string
        };

  return range;
};

export const generateZonedStartTime = (
  props?: GenerateZonedStartTimeProps,
  timeZone?: string
): {
  utc: string;
  zoned: string;
  date: Date;
} => generateZonedTimestamp(timeZone || 'UTC', props || {});

export const getZonedStartTime = (
  timeZone?: string,
  options?: {
    subtractSeconds?: number;
    subtractDays?: number;
    startOfDay?: boolean;
  }
): {
  zoned: string;
  utc: string;
  date: Date;
} => generateZonedTimestamp(timeZone || 'UTC', options || {});

// gets the current time in a specific timeZone
// redirecting to newer funtion
export const getCurrentZonedTime = (timeZone?: string, endOfDay?: boolean): GetCUrrentZonedTimeReturnProps => {
  if (typeof timeZone !== 'string') timeZone = timeZone || 'UTC';

  const x = 5;
  // get the current MS timestamp and convert to seconds
  const utcSeconds: number = new Date().getTime() / 1000;
  // round to the nearest x seconds and convert
  const utcSecondsRounded = Math.round(utcSeconds / x) * x;
  // make a new timestamp from the rounded seconds
  const newNow = new Date(utcSecondsRounded * 1000);
  let utcIso = newNow.toISOString();

  if (endOfDay) {
    const [dateGrp] = utcIso.split('T')
    const [dateStr] = dateGrp.split('Z')
    utcIso = `${dateStr}T23:59:59Z`
  }

  // Convert to the specified timezone
  const zonedIso = convertToZonedTimestamp(utcIso, timeZone) as string;

  return {
    zoned: zonedIso,
    string: zonedIso,
    utc: utcIso,
    date: newNow
  };
};

/** this loops through an array and retuns the latest string timestamp */
export const getLastTimestamp = (
  dateKey: string,
  options?: {
    data?: Record<string, unknown>[];
  }
): string | undefined =>
  !options?.data
    ? undefined
    : options.data.reduce((acc, item) => {
        const date = item?.[dateKey] as string;
        if (!date) return acc;
        if (acc === '') acc = date;
        if (acc < date) acc = date;
        return acc;
      }, '');

// this can be removed
export const getDateTime = (dateString: string): string[] => {
  const [date, times] = dateString.split('T');
  const [time] = times.split('+');
  return [date, time];
};

// converts an iso timestamp with the timezone offset
/** please use  convertToZonedTimestamp */
export const formatZonedTimestamp = (timestamp: string, tz: string, format?: string): string =>
  formatInTimeZone(parseISO(timestamp), tz, format || 'P, p');

export const convertDateToString = (
  date: Date,
  formatting?: string,
  options?: {
    // takes a date object with the local timezone attached to it,
    // converts the timestamp to UTC time
    convertToUTC?: boolean;
    timeZone?: string;
  }
): string => {
  formatting = formatting || 'api-call';

  const timeZone = options?.timeZone || 'UTC';

  const formats: Record<string, string> = {
    'chart-axis': 'MMM/d',
    'api-call': `yyyy-MM-dd'T'HH:mm:ssXXX`
  };

  const formatter = formats?.[formatting];
  const formatted = convertToZonedTimestamp(date, timeZone, formatter) as string;

  return options?.convertToUTC
    ? (convertToZonedTimestamp(formatted, 'utc') as string)
    : (formatted as string);
};

// converts seconds to 'hours as a number'
export const convertSecondsToHours = (seconds: number, round?: boolean): number => {
  const val = seconds / 3600;
  return round ? Math.ceil(val * 2) / 2 : val;
};

// converts 'hh:mm:ss' to seconds
export const convertTimeToSeconds = (time?: string): number => {
  if (!time) return -1;
  const parsedTime = parse(time, 'HH:mm:ss', new Date());
  return getHours(parsedTime) * 3600 + getMinutes(parsedTime) * 60 + getSeconds(parsedTime);
};

export const convertSecondsToTime = (
  seconds: unknown,
  formatType?: 'small' | 'hours' | 'long' | 'hr-min' | 'h:m:s' | 'h:m' | 'hrs' | 'h,rs' | 'string' | "decimal-hours" | string,
): string => {

  if (typeof seconds === 'string') seconds = Number(seconds);
  if (typeof seconds !== 'number') return '';


  if (formatType === 'hrs' && seconds === 0 || formatType === 'h,rs' && seconds === 0) return '0'
  if (formatType === 'small' && seconds === 0) return '00 sec'
  if (seconds === 0) return '00:00:00'

  const hours: string | number = Math.floor(seconds / 3600);

  seconds -= hours * 3600;

  const minutes = Math.floor(seconds / 60);

  seconds -= minutes * 60;
  seconds = Math.floor(seconds);
  seconds = Number(seconds) < 1 ? 1 : seconds;

  const showMin = minutes < 10 ? `0${minutes}` : minutes;
  const showSec = Number(seconds) < 10 ? `0${seconds}` : seconds;

  let formatted = `${hours || '00'}:${showMin || '00'}:${showSec || '00'}`;

  // costom formatter, "hours:minutes:seconds" leave out the seconds to now show them and set to 0 if you don't want to use
  if (formatType?.includes(':') || formatType?.includes('#') || formatType === 'h') {
    const splitter = formatType.includes('#') ? '#' : ':';
    const [customH, customMx, customSx] = formatType.split(splitter)
    const trim  = {
      customS: customSx?.includes('x') ? true : false,
      customM: customMx?.includes('x') ? true : false,
    }
    const customS = trim?.customS ? customSx?.replace('x', '') : customSx    
    const customM = trim?.customM ? customMx?.replace('x', '') : customMx
    
    formatted = customH === '00' || !hours ? `` : `${hours}${customH}`;
    if (splitter === ':') formatted = `${formatted} `;
    if (customM !== '0' && minutes) formatted = `${formatted}${minutes}${customM}`;
    if (splitter === ':') formatted = `${formatted} `;
    if ((!hours && !trim?.customS) && customS && customS !== '0' && showSec) formatted = `${formatted}${seconds}${customS}`;
  }

  if (formatType === 'hrs') {
    formatted = `${hours || `0`} hrs`;
  }

  if( formatType === 'h,rs') {
    formatted = `${hours || `0`}`;
    if(hours === 0) formatted = `${ (minutes / 60).toFixed(3) }`; // 0.47hrs
  }

  if (formatType === 'long' || formatType === 'hr-min') {
    formatted = `${hours || minutes || seconds}`;
    if (hours) formatted += ` hr`;
    if (minutes) formatted += ` ${showMin} min`;
  }

  if (formatType === 'small') {
    formatted = ``;
    if (hours) formatted += `${hours} hr `;
    if (showMin) formatted += `${showMin} min`;
  }

  // New format: decimal-hours (e.g., 90 min = 1.5)
  if (formatType === 'decimal-hours') {
    const decimalHours = (Number(hours) + minutes / 60 + Number(seconds) / 3600);
    
    formatted = decimalHours.toFixed(2);
  }

  return formatted;
};

export const toISO8601 = (date: Date, tz?: string): string => {
  if (tz) {
    const zonedDate = utcToZonedTime(date, tz);
    return tzFormat(zonedDate, "yyyy-MM-dd'T'HH:mm:ssXXX", { timeZone: tz });
  }
  return formatISO(date);
};

export const toFormat = (date: Date, formatDateTime: string): string => {
 return tzFormat(date, formatDateTime);
}

export const convertToRelativeTime = (createdAt: Date | string):string => {
  if (!createdAt) {
    return 'N/A';
  }

  const createdDate = new Date(createdAt);
  const currentDate = new Date();

  // Calculate the difference in time
  const timeDifference = currentDate.getTime() - createdDate.getTime();

  // Convert the time difference to days
  const daysDifference = Math.floor(timeDifference / (1000 * 60 * 60 * 24));

  if (daysDifference === 0) {
    return 'Today';
  } else if (daysDifference === 1) {
    return '1 day ago';
  } else {
    return `${daysDifference} days ago`;
  }
}